import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth, formatApiError } from '@/App';
import { EnvelopeSimple, Lock, GoogleLogo, ArrowRight, Phone, MapPin } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/217o5tly_Marema_Logo.jpg";

const ClientLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      
      if (user.role === 'admin' || user.role === 'staff') {
        navigate('/admin');
      } else {
        navigate('/portal');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/portal';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Marema Consulting" className="h-16 w-auto" />
          </Link>
        </div>
      </header>

      {/* Hero Banner */}
      <div 
        className="relative h-[200px] bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')`,
        }}
      >
        <div className="absolute inset-0 bg-[#4CAF50]/80"></div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-light text-white">Client Portal</h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Info */}
          <div className="lg:pr-8">
            <h2 className="text-3xl font-light text-gray-800 mb-6">
              Track Your Cases
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Access your client portal to track the status of your municipal account services, upload documents, and stay updated on your case progress.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#4CAF50]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#4CAF50] font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">New Requests</h3>
                  <p className="text-sm text-gray-600">Submit new service requests directly through the portal</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#4CAF50]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#4CAF50] font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Document Upload</h3>
                  <p className="text-sm text-gray-600">Securely submit required documents for your applications</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#4CAF50]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#4CAF50] font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Real-time Updates</h3>
                  <p className="text-sm text-gray-600">See live status updates on all your active service requests</p>
                </div>
              </div>
            </div>

            <div className="bg-[#4CAF50] p-6 text-white">
              <p className="text-sm text-white/80 mb-2">Need assistance?</p>
              <p className="font-medium mb-3">Contact our support team</p>
              <div className="space-y-2 text-sm text-white/90">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+27 10 500 9477</span>
                </div>
                <div className="flex items-center gap-2">
                  <EnvelopeSimple className="w-4 h-4" />
                  <span>info@maremaconsulting.co.za</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>288 on Kent, Ferndale, Randburg</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Login Form */}
          <div className="bg-white p-8 shadow-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Sign In to Your Portal</h2>
            <p className="text-gray-500 mb-6">Access your case information and documents</p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="client-login-form">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">Email Address</Label>
                <div className="relative">
                  <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@company.com"
                    className="pl-10 border-gray-300 rounded-none bg-gray-50 focus:ring-[#4CAF50] focus:border-[#4CAF50] h-12"
                    data-testid="client-email-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-[#4CAF50] hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                    className="pl-10 border-gray-300 rounded-none bg-gray-50 focus:ring-[#4CAF50] focus:border-[#4CAF50] h-12"
                    data-testid="client-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none h-12 font-medium"
                data-testid="client-login-btn"
              >
                {isLoading ? 'Signing in...' : 'Sign In to Portal'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-4 text-gray-500">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full border-2 border-gray-200 rounded-none h-12 font-medium hover:bg-gray-50 text-gray-700"
              data-testid="client-google-login-btn"
            >
              <GoogleLogo className="w-5 h-5 mr-2" weight="bold" />
              Sign in with Google
            </Button>

            <p className="mt-6 text-center text-sm text-gray-500">
              Don't have an account?{' '}
              <Link to="/portal/register" className="text-[#4CAF50] font-medium hover:underline" data-testid="register-link">
                Register here
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-6 mt-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Marema Consulting. All rights reserved.
          </p>
          <Link to="/" className="text-sm text-[#4CAF50] hover:underline flex items-center gap-1">
            Back to website <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default ClientLoginPage;
